import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { captureException } from '@sentry/angular';
import deepDiff from 'deep-diff';
import { combineLatest, delay, filter, map, Observable, startWith, take } from 'rxjs';
import { PromoPopupComponent, PromoPopupData } from 'src/app/components/promotion/promo-popup.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PlanService } from 'src/app/services/plan/plan.service';
import { Path, RoutePath } from 'src/app/shared/route';
import { Client, Roles } from 'src/app/types/identity';
import { LivePromotion, PlanUsage } from 'src/app/types/plan';

/** Long enough for the app shell to render first, so the popup doesn't compete with it. */
export const PROMO_POPUP_DELAY_MS = 1500;

/**
 * Shows a running sale's popup once per shop, and again whenever the merchant asks for it from the
 * header. GET /plan says which sale is running and whether this shop has seen its popup; that flag
 * lives on the shop (`promotionsSeen`), so "once" holds across browsers and staff accounts. The
 * automatic popup goes to every shop, whatever its plan, once onboarding is done; never on /plans,
 * where the merchant is already looking at the deal, and never over another dialog.
 */
@Injectable({
  providedIn: 'root',
})
export class PromoPopupService {
  private readonly planService = inject(PlanService);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  /** "/plans" (and "/plans/contact" under it). */
  private readonly plansUrl = '/' + RoutePath.getPath(Path['plans']).relativePath.join('/');

  /** The popup was opened in this session, either way: the automatic one never follows. */
  private opened = false;

  /** The sale running now, for a shop (never an admin), or null: drives the header's button. */
  readonly livePromotion$: Observable<LivePromotion | null> = combineLatest([
    this.planService.plan$,
    this.authService.user$,
  ]).pipe(map(([usage, identity]) => (identity?.role === Roles.Client ? (usage?.promotion ?? null) : null)));

  /** Watches for the moment the popup should show, and shows it once in this session. */
  start() {
    const navigated$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
    );

    return combineLatest([this.planService.plan$, this.authService.user$, navigated$])
      .pipe(
        // A shop's identity is its Client document; admins never see the popup (shouldShow).
        map(([usage, identity]) => ({ usage, client: identity as Client | undefined })),
        filter(({ usage, client }) => this.shouldShow(usage, client)),
        take(1),
        delay(PROMO_POPUP_DELAY_MS),
        // Looked at again after the wait: the merchant may have opened a dialog or /plans meanwhile.
        filter(({ usage, client }) => this.shouldShow(usage, client)),
      )
      .subscribe(({ usage, client }) => this.show(usage!, client!));
  }

  /** Opens the popup on the merchant's request (the header's button), seen before or not. */
  open() {
    combineLatest([this.planService.plan$, this.authService.user$])
      .pipe(take(1))
      .subscribe(([usage, identity]) => {
        if (usage?.promotion && identity?.role === Roles.Client) this.show(usage, identity as Client);
      });
  }

  private shouldShow(usage: PlanUsage | undefined, client: Client | undefined) {
    const promotion = usage?.promotion;

    return Boolean(
      promotion &&
      !promotion.seen &&
      !this.opened &&
      client &&
      client.role === Roles.Client &&
      client.finishedOnboarding &&
      !this.router.url.startsWith(this.plansUrl) &&
      this.dialog.openDialogs.length === 0,
    );
  }

  private show(usage: PlanUsage, client: Client) {
    const promotion = usage.promotion!;
    const data: PromoPopupData = { promotion };

    this.opened = true;
    this.dialog.open(PromoPopupComponent, {
      data,
      maxWidth: '480px',
      width: 'calc(100vw - 32px)',
      panelClass: 'promo-popup-panel',
      ariaLabelledBy: 'promo-popup-title',
    });

    const seen = client.promotionsSeen ?? {};

    if (promotion.seen || seen[promotion.key]) return;

    // Seen when it opens, not when it closes, so closing the tab counts too. Best effort: a failed
    // write is reported, and only means the popup may show again in a later session.
    this.authService
      .updateProfile({
        diffs:
          deepDiff(
            { promotionsSeen: seen },
            { promotionsSeen: { ...seen, [promotion.key]: new Date().toISOString() } },
          ) ?? [],
      })
      .subscribe({ error: (error) => captureException(error) });
  }
}
